import { useEffect, useState, Fragment } from "react";
import { Link } from "react-router-dom";
import {
  AreaChart, Area, ResponsiveContainer, XAxis, YAxis, Tooltip as RTooltip, CartesianGrid,
  RadialBarChart, RadialBar, PieChart, Pie, Cell,
} from "recharts";
import {
  ClipboardList, ListChecks, AlertTriangle, ThumbsUp, TrendingUp, ArrowUpRight,
  Loader2, FileDown,
} from "lucide-react";
import api from "../lib/api";
import { PageHeader } from "../components/AppLayout";
import { Card } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import { scoreColor, PRIORITY_STYLES, timeAgo } from "../components/shared";
import { Tooltip, TooltipTrigger, TooltipContent, TooltipProvider } from "../components/ui/tooltip";
import { toast } from "sonner";

const SUB = [
  { key: "health_safety", label: "Health & Safety" },
  { key: "training", label: "Training" },
  { key: "iso", label: "ISO" },
  { key: "esg", label: "ESG" },
  { key: "contractor", label: "Contractor" },
];

const PIE_COLORS = ["#4338ca", "#6366f1", "#8b5cf6", "#ec4899", "#f59e0b", "#10b981", "#06b6d4", "#ef4444"];

function KpiCard({ icon: Icon, label, value, tone, to }) {
  return (
    <Link to={to || "#"}>
      <Card className="group rounded-2xl border-border p-5 transition-all hover:-translate-y-1 hover:shadow-lg">
        <div className="flex items-start justify-between">
          <div className={`flex h-11 w-11 items-center justify-center rounded-xl ${tone}`}>
            <Icon className="h-5 w-5" />
          </div>
          <ArrowUpRight className="h-4 w-4 text-muted-foreground opacity-0 transition-opacity group-hover:opacity-100" />
        </div>
        <div className="mt-4 font-heading text-3xl font-bold tracking-tight">{value}</div>
        <div className="mt-0.5 text-sm text-muted-foreground">{label}</div>
      </Card>
    </Link>
  );
}

export default function Dashboard() {
  const [data, setData] = useState(null);

  useEffect(() => {
    api.get("/dashboard").then((r) => setData(r.data)).catch(() => toast.error("Failed to load dashboard"));
  }, []);

  if (!data)
    return <div className="flex h-64 items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-[hsl(var(--accent))]" /></div>;

  const overall = data.scores.overall;
  const gauge = [{ name: "score", value: overall, fill: scoreColor(overall) }];

  return (
    <div>
      <PageHeader title="Compliance Health" subtitle="Live organisational compliance overview across all modules.">
        <Button variant="outline" className="rounded-full" onClick={() => toast.info("PDF export queued — you'll be notified when ready.")} data-testid="export-pdf-btn">
          <FileDown className="mr-2 h-4 w-4" /> Export PDF
        </Button>
      </PageHeader>

      {/* KPI row */}
      <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
        <KpiCard icon={ClipboardList} label="Total Reports" value={data.kpis.total_reports} tone="bg-blue-500/10 text-blue-600 dark:text-blue-400" to="/app/reports" />
        <KpiCard icon={ListChecks} label="Open Actions" value={data.kpis.open_actions} tone="bg-violet-500/10 text-violet-600 dark:text-violet-400" to="/app/actions" />
        <KpiCard icon={AlertTriangle} label="Overdue Actions" value={data.kpis.overdue_actions} tone="bg-red-500/10 text-red-600 dark:text-red-400" to="/app/actions" />
        <KpiCard icon={ThumbsUp} label="Positive Spots" value={data.kpis.positive_observations} tone="bg-emerald-500/10 text-emerald-600 dark:text-emerald-400" to="/app/reports" />
      </div>

      <div className="mt-4 grid grid-cols-1 gap-4 lg:grid-cols-3">
        {/* Gauge + sub scores */}
        <Card className="rounded-2xl border-border p-6">
          <div className="text-sm font-semibold">Overall Compliance Score</div>
          <div className="relative mx-auto h-48 w-48">
            <ResponsiveContainer width="100%" height="100%">
              <RadialBarChart innerRadius="72%" outerRadius="100%" data={gauge} startAngle={90} endAngle={-270}>
                <RadialBar background dataKey="value" cornerRadius={20} />
              </RadialBarChart>
            </ResponsiveContainer>
            <div className="absolute inset-0 flex flex-col items-center justify-center">
              <div className="font-heading text-5xl font-bold tracking-tight" style={{ color: scoreColor(overall) }}>{overall}</div>
              <div className="text-xs text-muted-foreground">out of 100</div>
            </div>
          </div>
          <div className="mt-4 space-y-3">
            {SUB.map((s) => {
              const v = data.scores[s.key];
              return (
                <div key={s.key}>
                  <div className="mb-1 flex justify-between text-xs">
                    <span className="text-muted-foreground">{s.label}</span>
                    <span className="font-semibold">{v}</span>
                  </div>
                  <div className="h-1.5 w-full overflow-hidden rounded-full bg-secondary">
                    <div className="h-full rounded-full transition-all" style={{ width: `${v}%`, background: scoreColor(v) }} />
                  </div>
                </div>
              );
            })}
          </div>
        </Card>

        {/* Trend chart */}
        <Card className="rounded-2xl border-border p-6 lg:col-span-2">
          <div className="mb-4 flex items-center justify-between">
            <div className="text-sm font-semibold">Reporting & Actions Trend</div>
            <Badge variant="secondary" className="gap-1 rounded-full"><TrendingUp className="h-3 w-3" /> Last 6 months</Badge>
          </div>
          <ResponsiveContainer width="100%" height={260}>
            <AreaChart data={data.trend} margin={{ left: -20, right: 4 }}>
              <defs>
                <linearGradient id="gr" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="#4338ca" stopOpacity={0.35} /><stop offset="100%" stopColor="#4338ca" stopOpacity={0} /></linearGradient>
                <linearGradient id="ga" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="#10b981" stopOpacity={0.3} /><stop offset="100%" stopColor="#10b981" stopOpacity={0} /></linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" vertical={false} />
              <XAxis dataKey="month" tick={{ fontSize: 12, fill: "hsl(var(--muted-foreground))" }} axisLine={false} tickLine={false} />
              <YAxis tick={{ fontSize: 12, fill: "hsl(var(--muted-foreground))" }} axisLine={false} tickLine={false} />
              <RTooltip contentStyle={{ background: "hsl(var(--card))", border: "1px solid hsl(var(--border))", borderRadius: 12, fontSize: 12 }} />
              <Area type="monotone" dataKey="reports" stroke="#4338ca" strokeWidth={2.5} fill="url(#gr)" name="Reports" />
              <Area type="monotone" dataKey="actions" stroke="#10b981" strokeWidth={2.5} fill="url(#ga)" name="Actions" />
            </AreaChart>
          </ResponsiveContainer>
        </Card>
      </div>

      <div className="mt-4 grid grid-cols-1 gap-4 lg:grid-cols-3">
        {/* Category pie */}
        <Card className="rounded-2xl border-border p-6">
          <div className="mb-2 text-sm font-semibold">Reports by Category</div>
          <ResponsiveContainer width="100%" height={230}>
            <PieChart>
              <Pie data={data.category_breakdown} dataKey="value" nameKey="name" innerRadius={50} outerRadius={90} paddingAngle={2}>
                {data.category_breakdown.map((_, i) => <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />)}
              </Pie>
              <RTooltip contentStyle={{ background: "hsl(var(--card))", border: "1px solid hsl(var(--border))", borderRadius: 12, fontSize: 12 }} />
            </PieChart>
          </ResponsiveContainer>
          <div className="mt-2 space-y-1.5">
            {data.category_breakdown.slice(0, 4).map((c, i) => (
              <div key={c.name} className="flex items-center justify-between text-xs">
                <span className="flex items-center gap-2"><span className="h-2.5 w-2.5 rounded-full" style={{ background: PIE_COLORS[i % PIE_COLORS.length] }} />{c.name}</span>
                <span className="font-semibold">{c.value}</span>
              </div>
            ))}
          </div>
        </Card>

        {/* Risk heatmap — genuine 5x5 from ISO Risk Register */}
        <Card className="rounded-2xl border-border p-6" data-testid="risk-heatmap">
          <div className="mb-1 text-sm font-semibold">Risk Heatmap</div>
          <div className="mb-4 text-xs text-muted-foreground">ISO Risk Register · Severity × Likelihood (rating = S × L)</div>
          <div className="grid grid-cols-[1.4rem_auto] gap-2">
            <div className="flex items-center justify-center">
              <span className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground" style={{ writingMode: "vertical-rl", transform: "rotate(180deg)" }}>Severity</span>
            </div>
            <div>
              <TooltipProvider delayDuration={80}>
              <div className="grid grid-cols-[1.1rem_repeat(5,1fr)] gap-1.5">
                {[5, 4, 3, 2, 1].map((sev) => (
                  <Fragment key={sev}>
                    <div className="flex items-center justify-center text-[10px] font-medium text-muted-foreground">{sev}</div>
                    {[1, 2, 3, 4, 5].map((lik) => {
                      const val = data.risk_matrix?.[sev - 1]?.[lik - 1] || 0;
                      const rating = sev * lik;
                      const band = rating >= 15 ? "Extreme" : rating >= 10 ? "High" : rating >= 5 ? "Medium" : "Low";
                      const color = rating >= 15 ? "#ef4444" : rating >= 10 ? "#f59e0b" : rating >= 5 ? "#eab308" : "#10b981";
                      const txt = rating >= 10 ? "#ffffff" : "#0B1B4D";
                      const cellRisks = (data.risks || []).filter((r) => r.severity === sev && r.likelihood === lik);
                      return (
                        <Tooltip key={lik}>
                          <TooltipTrigger asChild>
                            <div data-testid={`risk-cell-${sev}-${lik}`}
                              className="flex aspect-square cursor-default items-center justify-center rounded-lg text-sm font-bold outline-none transition-transform hover:scale-[1.06]"
                              style={{ background: color, opacity: val ? 1 : 0.25, color: txt }}>
                              {val || ""}
                            </div>
                          </TooltipTrigger>
                          <TooltipContent side="top" className="max-w-[240px] bg-card text-card-foreground shadow-lg ring-1 ring-border">
                            <div className="flex items-center justify-between gap-3">
                              <span className="text-[11px] font-semibold">Severity {sev} × Likelihood {lik}</span>
                              <span className="rounded-full px-1.5 py-0.5 text-[10px] font-bold text-white" style={{ background: color }}>{band} · {rating}</span>
                            </div>
                            {cellRisks.length ? (
                              <ul className="mt-1.5 space-y-1">
                                {cellRisks.map((r, i) => (
                                  <li key={i} className="text-[11px] leading-tight">
                                    <span className="font-medium">{r.description}</span>
                                    <span className="text-muted-foreground"> — {r.owner} · {r.status}</span>
                                  </li>
                                ))}
                              </ul>
                            ) : (
                              <div className="mt-1 text-[11px] text-muted-foreground">No registered risks in this cell.</div>
                            )}
                          </TooltipContent>
                        </Tooltip>
                      );
                    })}
                  </Fragment>
                ))}
                <div />
                {[1, 2, 3, 4, 5].map((lik) => (
                  <div key={lik} className="text-center text-[10px] font-medium text-muted-foreground">{lik}</div>
                ))}
              </div>
              </TooltipProvider>
              <div className="mt-1.5 text-center text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">Likelihood</div>
            </div>
          </div>
          <div className="mt-4 flex flex-wrap items-center gap-x-3 gap-y-1 text-[10px] text-muted-foreground">
            {[["#10b981", "Low (1–4)"], ["#eab308", "Medium (5–9)"], ["#f59e0b", "High (10–14)"], ["#ef4444", "Extreme (15–25)"]].map(([c, l]) => (
              <span key={l} className="flex items-center gap-1"><span className="h-2.5 w-2.5 rounded-sm" style={{ background: c }} />{l}</span>
            ))}
          </div>
        </Card>

        {/* Recent reports */}
        <Card className="rounded-2xl border-border p-6">
          <div className="mb-3 flex items-center justify-between">
            <div className="text-sm font-semibold">Recent Reports</div>
            <Link to="/app/reports" className="text-xs font-medium text-[hsl(var(--accent))]">View all</Link>
          </div>
          <div className="space-y-3">
            {data.recent_reports.map((r) => (
              <div key={r.id} className="flex items-start gap-3">
                <span className={`mt-1.5 h-2 w-2 shrink-0 rounded-full ${r.priority === "high" ? "bg-red-500" : r.priority === "medium" ? "bg-amber-500" : "bg-emerald-500"}`} />
                <div className="min-w-0 flex-1">
                  <div className="truncate text-sm font-medium">{r.title}</div>
                  <div className="flex items-center gap-2 text-xs text-muted-foreground">
                    <span>{r.category}</span>·<span>{timeAgo(r.created_at)}</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </Card>
      </div>
    </div>
  );
}
